import React, { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import VideoCard from '../common/VideoCard';
import { __ } from '../../Lang.js';

const PlaylistRow = ({ title, url, fetchUrl, filters, initialVideos = [], isMobile: isMobileProp }) => {
  const [videos, setVideos] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileProp || false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pendingPageAdvance, setPendingPageAdvance] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const videosPerPage = isMobile ? 1 : 3;

  if (initialVideos.length == 0) {
    console.error('PlaylistRow initialized with no videos for playlist:', title || 'Untitled');
  }

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (initialVideos && initialVideos.length > 0) {
      setVideos(initialVideos);
      setOffset(initialVideos.length);
      if (initialVideos.length < videosPerPage) setHasMore(false);
    } else {
      loadVideos();
    }
  }, []);

  // Handle page advancement after videos load
  useEffect(() => {
    if (pendingPageAdvance !== null && !isLoading) {
      setCurrentPage(pendingPageAdvance);
      setPendingPageAdvance(null);
    }
  }, [videos.length, isLoading, pendingPageAdvance]);

  const loadVideos = async () => {
    if (!hasMore || isLoading) return;
    setIsLoading(true);
    
    
    try {
      const res = await fetch(fetchUrl + `&offset=${offset}&limit=${videosPerPage}`);
      const data = await res.json();

      if (Array.isArray(data.videos) && data.videos.length > 0) {
        const updated = [...videos, ...data.videos];
        setVideos(updated);
        setOffset(updated.length);

        if (data.videos.length < videosPerPage) setHasMore(false);
      } else {
        setHasMore(false);
      }
    } catch (e) {
      console.error('Error loading playlist:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const goToNextPage = async (e) => {
    
    // Prevent event bubbling and multiple calls
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent multiple simultaneous calls with debounce
    if (isLoading || isProcessing) {
      return;
    }
    
    setIsProcessing(true);
    
    const nextPage = currentPage + 1;
    const startIndex = nextPage * videosPerPage;
    
    try {
      if (startIndex >= videos.length && hasMore) {
        setPendingPageAdvance(nextPage + 1); 
        await loadVideos();
      } else {
        setCurrentPage(nextPage);
      }
    } finally {
      // Reset processing flag after a longer delay
      setTimeout(() => {
        setIsProcessing(false);
      }, 1000);
    }
  };

  const getCurrentVideos = () => {
    const start = currentPage * videosPerPage;
    const end = start + videosPerPage;
    const currentVideos = videos.slice(start, end);
    return currentVideos;
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const totalItems = videos.length || getCurrentVideos().length;

  return (
    <div className="playlist-row">
      <div className="playlist-row__container">
        <div className={`playlist-row__header ${isMobile ? 'playlist-row__header--mobile' : ''}`}>
          {isMobile ? (
            <>
              <div className="playlist-row__title-mobile">
                <h2 className="playlist-row__title">{title || 'Recommended Content'}</h2>
              </div>
              <div className="playlist-row__controls-mobile">
                <div className="playlist-row__view-all-mobile">
                  <Link href={url} className="playlist-row__view-link" color="inherit" underline="hover">{ __('VIEW ALL') } →</Link>
                </div>
                <div className="playlist-row__nav-buttons">
                  <IconButton
                    className="playlist-row__nav-btn"
                    disabled={currentPage === 0}
                    onClick={goToPrevPage}
                    aria-label="Previous"
                    sx={{ width: 30, height: 30, backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '50%', p: 0 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#333" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6"/>
                    </svg>
                  </IconButton>
                  <button 
                    className="playlist-row__nav-btn"
                    disabled={!hasMore && currentPage >= Math.ceil(videos.length / videosPerPage) - 1}
                    onClick={goToNextPage}
                    aria-label="Next"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#333" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="playlist-row__title-group">
                <h2 className="playlist-row__title">{title || 'Recommended Content'}</h2>
                <span className="playlist-row__view-all">
                  - <Link href={url} className="playlist-row__view-link" color="inherit" underline="hover">{ __('VIEW ALL') } →</Link>
                </span>
              </div>
              <div className="playlist-row__nav-buttons">
                <button
                  className="playlist-row__nav-btn"
                  disabled={currentPage === 0}
                  onClick={goToPrevPage}
                  aria-label="Previous"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#333" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6"/>
                  </svg>
                </button>
                <IconButton
                  className="playlist-row__nav-btn"
                  disabled={!hasMore && currentPage >= Math.ceil(videos.length / videosPerPage) - 1}
                  onClick={goToNextPage}
                  aria-label="Next"
                  sx={{ width: 30, height: 30, backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '50%', p: 0 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#333" strokeWidth="2">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </IconButton>
              </div>
            </>
          )}
        </div>

        <div className={`playlist-row__grid ${isMobile ? 'mobile' : ''}`}>
        {(getCurrentVideos() || [])
          .filter(video => video && typeof video === 'object' && video.uniqid)
          .map((video, index) => {
            const position = currentPage * videosPerPage + index + 1;
            return (
              <div key={video.uniqid} className="playlist-row__item">
                <VideoCard 
                  video={video}
                />
              </div>
            );
          })}
        </div>

        {isLoading && (
          <div className="playlist-row__loading">
            {__('Loading more videos...')}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaylistRow;